import { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { createPortal } from 'react-dom';
import { X, CheckSquare, Clock, CloudSun, Soundwave, ShieldCheck, HourglassSplit, Controller, Stars, GraphUp, MusicNoteBeamed, CircleHalf, Terminal, MoonStars, BarChartLine, Eye, BarChartFill } from 'react-bootstrap-icons';
import { Container, Row, Col } from 'react-bootstrap';
import TrackVisibility from 'react-on-screen';
import { useTranslation } from 'react-i18next';
import useTiltEffect from '../hooks/useTiltEffect';

import './Apps.css';

const TodoApp = lazy(() => import('../apps/TodoApp/TodoApp'));
const MetronomeApp = lazy(() => import('../apps/MetronomeApp/MetronomeApp'));
const WeatherApp = lazy(() => import('../apps/WeatherApp/WeatherApp'));
const PomodoroApp = lazy(() => import('../apps/PomodoroApp/PomodoroApp'));
const SEOAnalyzerApp = lazy(() => import('../apps/SEOAnalyzerApp/SEOAnalyzerApp'));
const TimerApp = lazy(() => import('../apps/TimerApp/TimerApp'));
const PrinceOfPersiaApp = lazy(() => import('../apps/PrinceOfPersiaApp/PrinceOfPersiaApp'));
const CyberOracle = lazy(() => import('../apps/CyberOracle/CyberOracle'));
const CyberGuard = lazy(() => import('../apps/CyberGuard/CyberGuard'));
const TrendsAnalyzer = lazy(() => import('../apps/TrendsAnalyzer/TrendsAnalyzer'));
const GuitarApp = lazy(() => import('../apps/GuitarApp/GuitarApp'));
const CircleApp = lazy(() => import('../apps/CircleApp/CircleApp'));
const UplinkApp = lazy(() => import('../apps/UplinkApp/UplinkApp'));
const KabbalahApp = lazy(() => import('../apps/KabbalahApp/KabbalahApp'));
const StatsApp = lazy(() => import('../apps/StatsApp/StatsApp'));
const SocialStatsApp = lazy(() => import('../apps/SocialStatsApp/SocialStatsApp'));
const IlluminatiApp = lazy(() => import('../apps/IlluminatiApp/IlluminatiApp'));
const TrucoApp = lazy(() => import('../apps/TrucoApp/TrucoApp'));


const APPS_CONFIG = [
    {
        id: 'todo',
        icon: CheckSquare,
        color: '#36AAA4',
        gradient: 'linear-gradient(135deg, #36AAA4, #4A2FBD)'
    },
    {
        id: 'metronome',
        icon: Soundwave,
        color: '#AA367C',
        gradient: 'linear-gradient(135deg, #AA367C, #4A2FBD)'
    },
    {
        id: 'weather',
        icon: CloudSun,
        color: '#60a5fa',
        gradient: 'linear-gradient(135deg, #60a5fa, #36AAA4)'
    },
    {
        id: 'pomodoro',
        icon: Clock,
        color: '#e74c3c',
        gradient: 'linear-gradient(135deg, #e74c3c, #3498db)'
    },
    {
        id: 'seo',
        icon: ShieldCheck,
        color: '#8b5cf6',
        gradient: 'linear-gradient(135deg, #8b5cf6, #36AAA4)'
    },
    {
        id: 'timer',
        icon: HourglassSplit,
        color: '#e74c3c',
        gradient: 'linear-gradient(135deg, #e74c3c, #f39c12)'
    },
    {
        id: 'persia',
        icon: Controller,
        color: '#f39c12',
        gradient: 'linear-gradient(135deg, #f39c12, #e74c3c)'
    },
    {
        id: 'oracle',
        icon: Stars,
        color: '#07faee',
        gradient: 'linear-gradient(135deg, #07faee, #36AAA4)'
    },
    {
        id: 'guard',
        icon: ShieldCheck,
        color: '#ef4444',
        gradient: 'linear-gradient(135deg, #ef4444, #3b82f6)'
    },
    {
        id: 'trends',
        icon: GraphUp,
        color: '#f97316',
        gradient: 'linear-gradient(135deg, #f97316, #facc15)'
    },
    {
        id: 'guitar',
        icon: MusicNoteBeamed,
        color: '#22c55e',
        gradient: 'linear-gradient(135deg, #22c55e, #8b5cf6)'
    },
    {
        id: 'circle',
        icon: CircleHalf,
        color: '#f59e0b',
        gradient: 'linear-gradient(135deg, #f59e0b, #ef4444)'
    },
    {
        id: 'uplink',
        icon: Terminal,
        color: '#ef4444',
        gradient: 'linear-gradient(135deg, #ef4444, #000000)'
    },
    {
        id: 'kabbalah',
        icon: MoonStars,
        color: '#8b5cf6',
        gradient: 'linear-gradient(135deg, #8b5cf6, #FFD700)'
    },
    {
        id: 'stats',
        icon: BarChartLine,
        color: '#0284c7',
        gradient: 'linear-gradient(135deg, #0284c7, #3b82f6)'
    },
    {
        id: 'socialstats',
        icon: BarChartFill,
        color: '#0ea5e9',
        gradient: 'linear-gradient(135deg, #0ea5e9, #10b981)'
    },
    {
        id: 'illuminati',
        icon: Eye,
        color: '#c9a94e',
        gradient: 'linear-gradient(135deg, #c9a94e, #1a1a2e)'
    },
    {
        id: 'truco',
        icon: Controller,
        color: '#10b981',
        gradient: 'linear-gradient(135deg, #10b981, #0284c7)'
    },
];

function Apps() {
    const { t } = useTranslation();
    const [activeApp, setActiveApp] = useState(null);
    const [userIPs, setUserIPs] = useState({ v4: '', v6: '' });
    const modalRef = useRef(null);
    useTiltEffect('.app-card');

    useEffect(() => {
        const fetchIPs = async () => {
            try {
                const [v4Resp, v6Resp] = await Promise.all([
                    fetch('https://api.ipify.org?format=json'),
                    fetch('https://api64.ipify.org?format=json')
                ]);
                const [v4Data, v6Data] = await Promise.all([
                    v4Resp.json(),
                    v6Resp.json()
                ]);
                setUserIPs({ 
                    v4: v4Data.ip, 
                    v6: v6Data.ip.includes(':') ? v6Data.ip : '' 
                });
            } catch (e) { console.error(e); }
        };
        fetchIPs();
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const gameId = params.get('gameId') || params.get('gameid');
        if (gameId) {
            setActiveApp('illuminati');
        }
        const trucoId = params.get('trucoId') || params.get('trucoid');
        if (trucoId) {
            setActiveApp('truco');
        }
    }, []);

    useEffect(() => {
        if (activeApp) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => { document.body.style.overflow = ''; };
    }, [activeApp]);

    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape') setActiveApp(null);
        };
        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, []);

    const openApp = (appId) => {
        setActiveApp(appId);
    };

    const closeApp = () => {
        setActiveApp(null);
    };

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) closeApp();
    };

    const renderApp = () => {
        switch (activeApp) {
            case 'todo': return <TodoApp />;
            case 'metronome': return <MetronomeApp />;
            case 'weather': return <WeatherApp />;
            case 'pomodoro': return <PomodoroApp />;
            case 'seo': return <SEOAnalyzerApp />;
            case 'timer': return <TimerApp />;
            case 'persia': return <PrinceOfPersiaApp />;
            case 'oracle': return <CyberOracle />;
            case 'guard': return <CyberGuard />;
            case 'trends': return <TrendsAnalyzer />;
            case 'guitar': return <GuitarApp />;
            case 'circle': return <CircleApp />;
            case 'uplink': return <UplinkApp />;
            case 'kabbalah': return <KabbalahApp />;
            case 'stats': return <StatsApp />;
            case 'socialstats': return <SocialStatsApp />;
            case 'illuminati': return <IlluminatiApp />;
            case 'truco': return <TrucoApp />;
            default: return null;
        }
    };

    return (
        <>
        <section className="apps" id="apps">
            <Container>
                <Row>
                    <Col size={12}>
                        <div className="apps-bx">
                            <TrackVisibility>
                                {({ isVisible }) =>
                                    <div className={isVisible ? "animate__animated animate__fadeIn" : ""}>
                                        <h2>{t('apps.title')}</h2>
                                        <p>{t('apps.description')}</p>
                                    </div>
                                }
                            </TrackVisibility>

                            <div className="apps-grid">
                                {APPS_CONFIG.map(app => {
                                    const Icon = app.icon;
                                    return (
                                        <div
                                            key={app.id}
                                            className="app-card"
                                            onClick={() => openApp(app.id)}
                                        >
                                            <div className="app-card-icon" style={{ background: app.gradient }}>
                                                <Icon size={40} />
                                            </div>
                                            <h4>{t(`apps.${app.id}.title`)}</h4>
                                            <div className="app-card-description">
                                                {t(`apps.${app.id}.description`)}
                                                {app.id === 'guard' && userIPs.v4 && (
                                                    <div className="app-card-ips mt-2" style={{ fontSize: '0.75rem' }}>
                                                        <span className="d-block" style={{ color: '#94a3b8' }}>
                                                            <strong style={{ color: '#f97316' }}>IPv4:</strong> {userIPs.v4}
                                                        </span>
                                                        {userIPs.v6 && (
                                                            <span className="d-block" style={{ color: '#94a3b8' }}>
                                                                 <strong style={{ color: '#f97316' }}>IPv6:</strong> {userIPs.v6}
                                                            </span>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="app-card-tech">
                                                {t(`apps.${app.id}.tech`)}
                                            </div>
                                            <span className="app-card-launch">
                                                {t('apps.launch')} &rarr;
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </Col>
                </Row>
            </Container>
        </section>

        {activeApp && createPortal(
            <div className="apps-modal-backdrop" onClick={handleBackdropClick} ref={modalRef}>
                <div className={`apps-modal ${activeApp === 'metronome' || activeApp === 'seo' || activeApp === 'stats' ? 'metronome-modal' : ''}`}>
                    <div className="apps-modal-header">
                        <h3>{t(`apps.${activeApp}.title`)}</h3>
                        <button className="apps-modal-close" onClick={closeApp}>
                            <X size={24} />
                        </button>
                    </div>
                    <div className="apps-modal-body">
                        <Suspense fallback={<div className="app-loading" style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>Loading...</div>}>
                            {renderApp()}
                        </Suspense>
                    </div>
                </div>
            </div>,
            document.body
        )}
        </>
    );
}

export default Apps;
